import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AppDispatch, RootState } from "../../store/store";
import { useAuth } from "../../hooks/useAuth";
import { apiFetch, ApiError } from "../../utils/api";
import LoadMoreButton from "../../components/LoadMoreButton";
import {
  fetchBookingsByHandyman,
  acceptBooking,
  declineBooking,
  completeBooking,
  cancelBooking,
} from "../../store/slice/booking/bookingThunks";

const statusColor: Record<string, string> = {
  PENDING: "text-yellow-600",
  CONFIRMED: "text-green-600",
  DECLINED: "text-red-600",
  COMPLETED: "text-blue-600",
  CANCELLED: "text-gray-500",
};

const WorkmanNaijaDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useAuth();
  const { bookings, pageInfo, loading, error } = useSelector(
    (state: RootState) => state.booking
  );

  // Bookings are addressed to a provider id, which is linked to this login by
  // email - look it up rather than asking the handyman to type it
  const [provider, setProvider] = useState<{ id: number; businessName?: string } | null>(null);
  const [providerLookup, setProviderLookup] = useState<"loading" | "found" | "none" | "error">(
    "loading"
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    apiFetch("/api/providers/me", {}, true)
      .then((data) => {
        if (cancelled) return;
        setProvider(data);
        setProviderLookup("found");
        dispatch(fetchBookingsByHandyman(data.id));
      })
      .catch((e) => {
        if (cancelled) return;
        setProviderLookup(e instanceof ApiError && e.status === 404 ? "none" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated]);

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Handyman Dashboard</h1>
        {provider?.businessName && (
          <p className="text-gray-500">{provider.businessName}</p>
        )}
      </div>

      {error && <p className="text-red-600">{error}</p>}

      {providerLookup === "none" && (
        <p className="text-gray-700">
          This account doesn't have a provider profile yet.{" "}
          <Link to="/register-provider" className="text-green-700 font-medium hover:underline">
            List your business
          </Link>{" "}
          using the same email to start receiving bookings.
        </p>
      )}
      {providerLookup === "error" && (
        <p className="text-red-600">Couldn't load your provider profile. Please try again.</p>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Booking Requests
        </h2>
        {providerLookup === "loading" || (loading && bookings.length === 0) ? (
          <p className="text-gray-500 text-sm">Loading bookings...</p>
        ) : bookings.length === 0 ? (
          <p className="text-gray-500 text-sm">No booking requests yet.</p>
        ) : (
          <ul className="space-y-2">
            {bookings.map((b) => (
              <li
                key={b.id}
                className="border border-gray-200 rounded-lg p-3 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    #{b.id} — {b.serviceType} at {b.location}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(b.bookingDate).toLocaleString()}
                  </p>
                  <span className={`font-semibold ${statusColor[b.status]}`}>
                    {b.status}
                  </span>
                </div>
                {b.status === "PENDING" && (
                  <div className="flex gap-2">
                    <button
                      disabled={loading}
                      onClick={() => dispatch(acceptBooking(b.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    >
                      Accept
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => dispatch(declineBooking(b.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
                    >
                      Decline
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => dispatch(cancelBooking(b.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {b.status === "CONFIRMED" && (
                  <div className="flex gap-2">
                    <button
                      disabled={loading}
                      onClick={() => dispatch(completeBooking(b.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      Mark Complete
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => dispatch(cancelBooking(b.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {provider && (
          <LoadMoreButton
            shown={bookings.length}
            total={pageInfo.totalElements}
            last={pageInfo.last}
            loading={loading}
            onClick={() => dispatch(fetchBookingsByHandyman(provider.id, pageInfo.page + 1))}
          />
        )}
      </div>
    </div>
  );
};

export default WorkmanNaijaDashboard;
